import range from 'lodash/range';
import round from 'lodash/round';
import type { QueryResult } from '@atlassian/jira-relay-apollo-migration-utils';
import type {
	deploymentFrequencyCoreRelayQuery$data,
	deploymentFrequencyCoreRelayQuery$variables,
} from '@atlassian/jira-relay/src/__generated__/deploymentFrequencyCoreRelayQuery.graphql';
import type { CloudId, ProjectId } from '@atlassian/jira-shared-types';
import type { Provider } from '@atlassian/jira-software-deployments-devops-provider';
import type { ServiceQueryResultWithFetch } from '@atlassian/jira-software-deployments-service-query-types';
import { WEEKS_IN_REPORT } from '../../common/constants';
import type {
	Count,
	DateRange,
	DeploymentFrequencyReport,
	DeploymentFrequencyReportWeekData,
	ProdDataState,
	DeploymentFrequencyReportWithProviderInfo,
} from '../../common/types';
import { getMaybeCombinedError } from '../../common/utils/errors';
import { getUserTimezone } from '../../common/utils/time-zone';

export const isProdDataEmpty = (data: DeploymentFrequencyReportWithProviderInfo): boolean =>
	data.weeksData.every(
		(weekData: DeploymentFrequencyReportWeekData) => weekData.deploymentCount.prod === 0,
	);
export const isDevelopmentDataEmpty = (data: DeploymentFrequencyReportWithProviderInfo): boolean =>
	data.weeksData.every(
		(weekData: DeploymentFrequencyReportWeekData) => weekData.deploymentCount.development === 0,
	);
export const isTestingDataEmpty = (data: DeploymentFrequencyReportWithProviderInfo): boolean =>
	data.weeksData.every(
		(weekData: DeploymentFrequencyReportWeekData) => weekData.deploymentCount.testing === 0,
	);
export const isStagingDataEmpty = (data: DeploymentFrequencyReportWithProviderInfo): boolean =>
	data.weeksData.every(
		(weekData: DeploymentFrequencyReportWeekData) => weekData.deploymentCount.staging === 0,
	);
export const isUnmappedDataEmpty = (data: DeploymentFrequencyReportWithProviderInfo): boolean =>
	data.weeksData.every(
		(weekData: DeploymentFrequencyReportWeekData) => weekData.deploymentCount.unmapped === 0,
	);

export const isDataAbsolutelyEmpty = (data: DeploymentFrequencyReportWithProviderInfo): boolean =>
	data.weeksData.every(
		(weekData: DeploymentFrequencyReportWeekData): boolean =>
			weekData.deploymentCount.development === 0 &&
			weekData.deploymentCount.testing === 0 &&
			weekData.deploymentCount.staging === 0 &&
			weekData.deploymentCount.prod === 0 &&
			weekData.deploymentCount.unmapped === 0 &&
			weekData.medianIssuesPerDeployment.prod === null,
	);

export const isProdDataInsufficient = (data: DeploymentFrequencyReportWithProviderInfo): boolean =>
	data.weeksData.filter(
		(weekData: DeploymentFrequencyReportWeekData) => weekData.deploymentCount.prod > 0,
	).length <= 3;

export const isDataAllUnmapped = (data: DeploymentFrequencyReportWithProviderInfo): boolean =>
	data.weeksData.every(
		(weekData: DeploymentFrequencyReportWeekData): boolean =>
			weekData.deploymentCount.development === 0 &&
			weekData.deploymentCount.testing === 0 &&
			weekData.deploymentCount.staging === 0 &&
			weekData.deploymentCount.prod === 0 &&
			weekData.medianIssuesPerDeployment.prod === 0,
	) &&
	data.weeksData.some(
		(weekData: DeploymentFrequencyReportWeekData): boolean => weekData.deploymentCount.unmapped > 0,
	);

export const isDataUnmappedFoundWithMissingPreProdButWithProd = (
	data: DeploymentFrequencyReportWithProviderInfo,
): boolean =>
	data.weeksData.some(
		(weekData: DeploymentFrequencyReportWeekData): boolean => weekData.deploymentCount.unmapped > 0,
	) &&
	(isDevelopmentDataEmpty(data) || isTestingDataEmpty(data) || isStagingDataEmpty(data)) &&
	!isProdDataEmpty(data);

export const isDataUnmappedFoundWithMissingProd = (
	data: DeploymentFrequencyReportWithProviderInfo,
): boolean =>
	data.weeksData.some(
		(weekData: DeploymentFrequencyReportWeekData): boolean => weekData.deploymentCount.unmapped > 0,
	) && isProdDataEmpty(data);

export const getProdDataState = (
	data: DeploymentFrequencyReportWithProviderInfo,
): ProdDataState => {
	if (isDataAbsolutelyEmpty(data)) {
		return 'COMPLETELY_EMPTY';
	}

	if (isProdDataEmpty(data)) {
		return 'NO_PROD_BUT_OTHER_ENV';
	}

	if (isProdDataInsufficient(data)) {
		return 'INSUFFICIENT_FOR_TREND';
	}

	return 'SUFFICIENT_FOR_TREND';
};

export const buildQueryVariables = (
	cloudId: CloudId,
	projectId: ProjectId,
	dateRange: DateRange,
): deploymentFrequencyCoreRelayQuery$variables => {
	const { startDateInclusive, endDateExclusive } = dateRange;

	return {
		filter: {
			cloudId,
			startFromInclusive: startDateInclusive.toISOString(),
			endAtExclusive: endDateExclusive.toISOString(),
			timezoneId: getUserTimezone(),
			resolution: {
				unit: 'WEEK',
				value: 1,
			},
			jiraProjectIds: [projectId],
		},
		deploymentFrequencyRollupType: {
			type: 'MEAN',
		},
		deploymentSizeRollupType: {
			type: 'PERCENTILE',
			percentile: 50,
		},
	};
};

const getQueryErrorMessageIfExist = (error?: Error): string =>
	error ? ` QueryErrorMessage: ${error.message}` : '';

type FrequencyData = {
	count: Count;
	dateTime: string;
};
type SizeData = {
	deploymentSize: Count | null;
	dateTime: string;
};
type FrequencySizeData = {
	frequencyDevData: FrequencyData;
	frequencyTestingData: FrequencyData;
	frequencyStgData: FrequencyData;
	frequencyProdData: FrequencyData;
	frequencyUnmappedData: FrequencyData;
	sizeProdData: SizeData;
};
type ValidatedData = {
	data: FrequencySizeData[];
	frequencyProdAverage: number;
	sizeProdMedian: number | null;
};
const getValidatedData = (
	queryResult: QueryResult<
		deploymentFrequencyCoreRelayQuery$variables,
		deploymentFrequencyCoreRelayQuery$data
	>,
): ValidatedData => {
	const deploymentFrequencyDevData = queryResult.data?.devOpsMetrics?.deploymentFrequencyDev?.data;
	const deploymentFrequencyTestingData =
		queryResult.data?.devOpsMetrics?.deploymentFrequencyTesting?.data;
	const deploymentFrequencyStgData = queryResult.data?.devOpsMetrics?.deploymentFrequencyStg?.data;
	const deploymentFrequencyProdData =
		queryResult.data?.devOpsMetrics?.deploymentFrequencyProd?.data;
	const deploymentFrequencyProdAverage =
		queryResult.data?.devOpsMetrics?.deploymentFrequencyProd?.aggregateData;
	const deploymentFrequencyUnmappedData =
		queryResult.data?.devOpsMetrics?.deploymentFrequencyUnmapped?.data;
	const deploymentSizeProdData = queryResult.data?.devOpsMetrics?.deploymentSizeProd?.data;
	const deploymentSizeProdMedian =
		queryResult.data?.devOpsMetrics?.deploymentSizeProd?.aggregateData;
	const { error: apolloError } = queryResult;
	const apolloErrorMessage = getQueryErrorMessageIfExist(apolloError);

	if (
		deploymentFrequencyDevData == null ||
		deploymentFrequencyTestingData == null ||
		deploymentFrequencyStgData == null ||
		deploymentFrequencyProdData == null ||
		deploymentFrequencyProdAverage == null ||
		deploymentFrequencyUnmappedData == null ||
		deploymentSizeProdData == null ||
		deploymentSizeProdMedian === undefined
	) {
		throw Error(
			`Deployment frequency report: queryResult has missing fields. ${apolloErrorMessage}. Stringified queryResult: ${JSON.stringify(
				{
					deploymentFrequencyDevData,
					deploymentFrequencyTestingData,
					deploymentFrequencyStgData,
					deploymentFrequencyProdData,
					deploymentFrequencyProdAverage,
					deploymentFrequencyUnmappedData,
					deploymentSizeProdData,
					deploymentSizeProdMedian,
				},
			)}`,
		);
	}

	return {
		// @ts-expect-error - TS2322 - Type '{ frequencyDevData: { count: number | null; dateTime: any; }; frequencyTestingData: { count: number | null; dateTime: any; }; frequencyStgData: { count: number | null; dateTime: any; }; frequencyProdData: { ...; }; frequencyUnmappedData: { ...; }; sizeProdData: { ...; }; }[]' is not assignable to type 'FrequencySizeData[]'.
		data: range(WEEKS_IN_REPORT).map((i: number) => {
			if (
				deploymentFrequencyDevData?.[i]?.count == null ||
				deploymentFrequencyDevData?.[i]?.dateTime == null ||
				deploymentFrequencyTestingData?.[i]?.count == null ||
				deploymentFrequencyTestingData?.[i]?.dateTime == null ||
				deploymentFrequencyStgData?.[i]?.count == null ||
				deploymentFrequencyStgData?.[i]?.dateTime == null ||
				deploymentFrequencyProdData?.[i]?.count == null ||
				deploymentFrequencyProdData?.[i]?.dateTime == null ||
				deploymentFrequencyUnmappedData?.[i]?.count == null ||
				deploymentFrequencyUnmappedData?.[i]?.dateTime == null ||
				deploymentSizeProdData?.[i]?.deploymentSize === undefined ||
				deploymentSizeProdData?.[i]?.dateTime == null
			) {
				throw new Error(
					`Deployment frequency report: some data is missing. ${apolloErrorMessage}. Stringified data: ${JSON.stringify(
						{
							frequencyDevData: deploymentFrequencyDevData[i],
							frequencyTestingData: deploymentFrequencyTestingData[i],
							frequencyStgData: deploymentFrequencyStgData[i],
							frequencyProdData: deploymentFrequencyProdData[i],
							frequencyUnmappedData: deploymentFrequencyUnmappedData[i],
							sizeProdData: deploymentSizeProdData[i],
						},
					)}`,
				);
			}

			return {
				frequencyDevData: {
					count: deploymentFrequencyDevData?.[i]?.count,
					dateTime: deploymentFrequencyDevData?.[i]?.dateTime,
				},
				frequencyTestingData: {
					count: deploymentFrequencyTestingData?.[i]?.count,
					dateTime: deploymentFrequencyTestingData?.[i]?.dateTime,
				},
				frequencyStgData: {
					count: deploymentFrequencyStgData?.[i]?.count,
					dateTime: deploymentFrequencyStgData?.[i]?.dateTime,
				},
				frequencyProdData: {
					count: deploymentFrequencyProdData?.[i]?.count,
					dateTime: deploymentFrequencyProdData?.[i]?.dateTime,
				},
				frequencyUnmappedData: {
					count: deploymentFrequencyUnmappedData?.[i]?.count,
					dateTime: deploymentFrequencyUnmappedData?.[i]?.dateTime,
				},
				sizeProdData: {
					deploymentSize: deploymentSizeProdData?.[i]?.deploymentSize,
					dateTime: deploymentSizeProdData?.[i]?.dateTime,
				},
			};
		}),
		frequencyProdAverage: deploymentFrequencyProdAverage,
		sizeProdMedian: deploymentSizeProdMedian,
	};
};

export const transformResult = (
	queryResult: QueryResult<
		deploymentFrequencyCoreRelayQuery$variables,
		deploymentFrequencyCoreRelayQuery$data
	>,
): ServiceQueryResultWithFetch<DeploymentFrequencyReport> => {
	const resultBase = {
		loading: queryResult.loading,
		error: queryResult.error,
		fetch: queryResult.refetch,
	};

	if (queryResult.loading) {
		return resultBase;
	}

	try {
		const { data, frequencyProdAverage, sizeProdMedian } = getValidatedData(queryResult);

		return {
			...resultBase,
			data: {
				averageDeploymentCount: round(frequencyProdAverage, 1),
				medianIssuesPerDeployment: sizeProdMedian === null ? null : round(sizeProdMedian, 1),
				weeksData: data.map(
					({
						frequencyDevData,
						frequencyTestingData,
						frequencyStgData,
						frequencyProdData,
						frequencyUnmappedData,
						sizeProdData,
					}: FrequencySizeData) => ({
						startDateInclusive: new Date(frequencyDevData.dateTime),
						deploymentCount: {
							development: frequencyDevData.count,
							testing: frequencyTestingData.count,
							staging: frequencyStgData.count,
							prod: frequencyProdData.count,
							unmapped: frequencyUnmappedData.count,
						},
						medianIssuesPerDeployment: {
							prod: sizeProdData.deploymentSize,
						},
					}),
				),
			},
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return {
			...resultBase,
			error: getMaybeCombinedError([queryResult.error, error]),
		};
	}
};

/**
 * This merges report data with deployment provider information packing it as `core data` to start viewing the report
 */
export const mergeResult = (
	{
		data: reportData,
		loading: reportLoading,
		error: reportError,
		fetch: reportFetch,
	}: ServiceQueryResultWithFetch<DeploymentFrequencyReport>,
	{
		data: providerData,
		loading: providerLoading,
		error: providerError,
		fetch: providerFetch,
	}: ServiceQueryResultWithFetch<Provider[]>,
): ServiceQueryResultWithFetch<DeploymentFrequencyReportWithProviderInfo> => {
	const loading = reportLoading || providerLoading;

	const error = getMaybeCombinedError([reportError, providerError]);
	const fetch = async () => {
		if (reportError && reportData == null) reportFetch();
		if (providerError && providerData == null) providerFetch();
	};
	const data =
		providerData && reportData
			? { ...reportData, hasDeploymentProvider: providerData.length > 0 }
			: undefined;

	return { loading, error, data, fetch };
};
